import { MaterialCommunityIcons } from '@expo/vector-icons';
import { AssetTag, useCurrentAccount } from '@gripp/shared-logic';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { CustomAlert } from '../customAlert';
import { AssetTagGalleryProps } from './types';

const TAG_WIDTH = 80;

// checks for firefox web browser
// https://stackoverflow.com/questions/49328382/browser-detection-in-reactjs
const isFirefox = typeof (window as any).InstallTrigger !== 'undefined';

export const AssetTagGallery = (props: AssetTagGalleryProps) => {
  return <AssetTagGalleryBase {...props} />;
};

const AssetTagGalleryBase = (props: AssetTagGalleryProps) => {
  const [assetTags, setAssetTags] = useState<AssetTag[]>(
    props.existingAssetTags
  );
  const TagPicker = props.tagPicker || (() => null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [contentContainerStyle, setContentContainerStyle] = useState({} as any);
  const { t } = useTranslation();
  const { account } = useCurrentAccount();
  const editable = props.editable ?? true;

  // Platform-specific permission logic:
  // Desktop (web): Only Global Admins can create tags
  // Mobile (iOS/Android): Workspace Admins and General Users can create tags (not Restricted Users)
  const canCreateTags =
    Platform.OS === 'web'
      ? !!account?.isGlobalAdmin()
      : !!account?.hasPermission('tag.update') ||
        !!account?.hasPermission('asset.create');

  const getHorizontalMediaGalleryWidth = useCallback((length: number) => {
    if (Platform.OS === 'web') {
      return (length - 1) * TAG_WIDTH + 80;
    } else {
      return (length + 1.1) * TAG_WIDTH;
    }
  }, []);

  useEffect(() => {
    setAssetTags(props.existingAssetTags);
    setContentContainerStyle({
      minWidth: getHorizontalMediaGalleryWidth(props.existingAssetTags.length),
      paddingRight: 8,
    });
  }, [getHorizontalMediaGalleryWidth, props.existingAssetTags]);

  const handleAdd = (newAssetTag: AssetTag) => {
    if (!newAssetTag) return;

    if (assetTags.some((t) => t.id === newAssetTag.id)) {
      setAlertTitle(t('asset.customAlert.tagAlreadyAdded'));
      setShowAlert(true);
      return;
    }

    setAssetTags((prevState) => [...prevState, newAssetTag]);

    props.onAssetTagAdded(newAssetTag);
    setContentContainerStyle({
      width: getHorizontalMediaGalleryWidth(assetTags.length + 1),
    });
  };

  const handleDelete = (assetTagId: string) => {
    setAssetTags((prevState) => {
      return prevState.filter((t) => t.id !== assetTagId);
    });
    props.onAssetTagDeleted(assetTagId);
    setContentContainerStyle({
      width: getHorizontalMediaGalleryWidth(assetTags.length - 0.1),
    });
  };

  return (
    <View style={styles.container}>
      {editable && canCreateTags && props.tagPicker && (
        <View style={styles.pickerContainer}>
          <TagPicker
            setAlertTitle={(title: string) => setAlertTitle(title)}
            setShowAlert={(visible: boolean) => setShowAlert(visible)}
            setTag={handleAdd}
          />
        </View>
      )}
      <ScrollView
        horizontal={true}
        showsHorizontalScrollIndicator={true}
        scrollEnabled={true}
        contentContainerStyle={[
          contentContainerStyle,
          { paddingTop: Platform.OS === 'web' ? 0 : 10 },
        ]}
        persistentScrollbar={true}
      >
        {assetTags.map((tag) => (
          <View style={styles.itemContainer} key={tag.id}>
            <View style={styles.tag}>
              {editable && (
                <Pressable
                  hitSlop={10}
                  style={styles.deleteButton}
                  onPress={() => handleDelete(tag.id)}
                >
                  <MaterialCommunityIcons
                    name="close"
                    color={Colors.black}
                    size={16}
                  />
                </Pressable>
              )}
              {props.onViewTagQr ? (
                <Pressable
                  hitSlop={8}
                  accessibilityRole="button"
                  accessibilityLabel={t('asset.qr.viewQrLabel', {
                    tagCode: tag.tagCode,
                  })}
                  onPress={() => props.onViewTagQr?.(tag)}
                >
                  <MaterialCommunityIcons
                    name="qrcode"
                    size={24}
                    color={Colors.neutral[800]}
                    style={isFirefox && styles.qrCodeIcon}
                  />
                </Pressable>
              ) : (
                <MaterialCommunityIcons
                  name="qrcode"
                  size={24}
                  color={Colors.neutral[800]}
                  style={isFirefox && styles.qrCodeIcon}
                />
              )}
              <Text style={styles.text}>{tag.tagCode}</Text>
            </View>
          </View>
        ))}
      </ScrollView>
      <CustomAlert
        visible={showAlert}
        hideAlert={() => setShowAlert(false)}
        title={alertTitle}
        secondaryButtonLabel={t('customAlert.ok')}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    flexDirection: 'row',
    display: 'flex',
    alignSelf: 'flex-start',
    justifyContent: 'space-between',
  },
  itemContainer: {
    marginLeft: 2,
    marginBottom: 4,
    marginRight: 8,
  },
  pickerContainer: {
    marginRight: 8,
    marginBottom: 4,
    marginTop: 10,
    marginLeft: 2,
    ...Platform.select({
      web: {
        marginTop: 0,
      },
    }),
  },
  tag: {
    width: 88,
    height: 88,
    borderRadius: 4,
    backgroundColor: '#F3F3F3',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  qrCodeIcon: {
    ...Platform.select({
      web: {
        bottom: 0,
      },
    }),
  },
  text: {
    alignSelf: 'center',
    fontSize: 13,
    fontWeight: '500',
    color: Colors.black,
  },
  deleteButton: {
    position: 'absolute',
    top: 6,
    right: 6,
    zIndex: 100,
  },
});
